import cx from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FunctionComponent, ReactNode } from 'react';
import useResizeObserver from 'use-resize-observer';

import { Analytics } from '@trello/atlassian-analytics';
import {
  BOARD_MIN_WIDTH,
  useSplitScreenSharedState,
} from '@trello/split-screen';
import type { PanelNames } from '@trello/split-screen';
import { getTestId } from '@trello/test-ids';
import type { SplitScreenTestIds } from '@trello/test-ids/';

import { SplitScreenResizeDivider } from './SplitScreenResizeDivider';

import styles from './SplitScreenPanel.less';

interface SplitScreenPanelProps {
  children?: ReactNode;
  panelName: PanelNames;
  isHidden?: boolean;
}

export const SplitScreenPanel: FunctionComponent<SplitScreenPanelProps> = ({
  children,
  isHidden,
  panelName,
}) => {
  const {
    updateResizedPanelWidth,
    resizeAllPanelsToDefault,
    plannerPosition,
    areMultiplePanelsOpen,
    panels,
    togglePanel,
  } = useSplitScreenSharedState();

  const storedPanelWidth = panels.panelWidths[panelName];

  const isRightmostPanel = useMemo(() => {
    if (panelName === 'inbox') return false;

    if (panelName === 'planner')
      return plannerPosition === 'rightmost' || !areMultiplePanelsOpen;

    if (panelName === 'board') return true;
  }, [panelName, plannerPosition, areMultiplePanelsOpen]);

  const shouldRenderDivider = panelName !== 'board';

  //we need this because board never actually resizes, so we need to observe its width for max width calculations
  const { width: observedPanelWidth, ref: panelRef } =
    useResizeObserver<HTMLDivElement>();

  const minWidth = storedPanelWidth.minWidth;
  const maxWidth = storedPanelWidth.maxWidth;

  useEffect(() => {
    //we need this because board never actually resizes, so we need to observe its width for max width calculations. We should ONLY do this for
    //board or whatever is the rightmost panel, though.
    //This will *not* update the flex-basis of the rightmost panel, but will effectively stop other panels from resizing past board's width.
    if (panelName === 'board' && !isHidden) {
      updateResizedPanelWidth(
        panelName,
        Math.max(observedPanelWidth ?? storedPanelWidth.width, BOARD_MIN_WIDTH),
      );
    }
  }, [
    isHidden,
    observedPanelWidth,
    panelName,
    updateResizedPanelWidth,
    storedPanelWidth.width,
  ]);

  //this is the in-flux panel width, used by the callbacks to set the width on the panel itself
  const [panelWidth, setPanelWidth] = useState(storedPanelWidth.width);
  const [previousWidth, setPreviousWidth] = useState(storedPanelWidth.width);
  const [isCollapsed, setIsCollapsed] = useState(false);

  //we need to disable transitions when we are loading and when the user is dragging the dividers
  const [enableTransitions, setEnableTransitions] = useState(true);

  const handleDoubleClick = useCallback(() => {
    resizeAllPanelsToDefault();
  }, [resizeAllPanelsToDefault]);

  const onDragStartCallback = useCallback(() => {
    setEnableTransitions(false);
  }, []);

  const draggingPanelCallback = useCallback(
    (newWidth: number) => {
      setPanelWidth(newWidth);

      if (newWidth === 0 || newWidth === minWidth) {
        //we re-enable transitions here so the collapse/re-expand feels a bit smoother during the drag
        setEnableTransitions(true);
      } else {
        setEnableTransitions(false);
      }

      setIsCollapsed(newWidth === 0);
    },
    [minWidth],
  );

  const dropPanelCallback = useCallback(
    (newWidth: number) => {
      setEnableTransitions(true);

      if (newWidth === 0) {
        togglePanel(panelName);
        setPanelWidth(previousWidth);
        updateResizedPanelWidth(panelName, previousWidth);
      } else {
        setPreviousWidth(newWidth);
        updateResizedPanelWidth(panelName, newWidth);

        Analytics.sendUIEvent({
          action: 'resized',
          actionSubject: 'panel',
          actionSubjectId: `${panelName}Panel`,
          source: 'personalProductivitySplitScreenResizingDivider',
          attributes: {
            isInboxOpen: panels.inbox,
            isPlannerOpen: panels.planner,
            isBoardOpen: panels.board,
            newPanelSize: newWidth,
          },
        });
      }

      setIsCollapsed(false);
    },
    [panelName, panels, previousWidth, togglePanel, updateResizedPanelWidth],
  );

  useEffect(() => {
    setEnableTransitions(true);
    setPanelWidth(storedPanelWidth.width);
    setPreviousWidth(storedPanelWidth.width);
  }, [storedPanelWidth.width]);

  const flexValue = useMemo(() => {
    if (isHidden) {
      return '0 0 0px';
    }

    if (!areMultiplePanelsOpen || isRightmostPanel) {
      return `1 1 ${panelWidth}px`;
    }

    if (!isRightmostPanel) {
      return `0 0 ${panelWidth}px`;
    }
  }, [isRightmostPanel, panelWidth, isHidden, areMultiplePanelsOpen]);

  const shouldHideDivider =
    (isRightmostPanel || isHidden || !areMultiplePanelsOpen) ?? true;

  return (
    <>
      <div
        className={cx(styles.panel, styles[panelName], {
          [styles.singlePanel]: !areMultiplePanelsOpen,
          [styles.panelTransitions]: enableTransitions,
          [styles.rightmostPanel]: isRightmostPanel,
          [styles.dragging]: !enableTransitions,
          [styles.hidden]: isHidden,
          [styles.collapsed]: isCollapsed,
        })}
        data-testid={getTestId<SplitScreenTestIds>('split-screen-panel')}
        ref={panelRef}
        style={{
          flex: flexValue,
        }}
        aria-hidden={isHidden}
      >
        {children}
      </div>
      {shouldRenderDivider && (
        <SplitScreenResizeDivider
          onDragStartCallback={onDragStartCallback}
          draggingPanelCallback={draggingPanelCallback}
          dropPanelCallback={dropPanelCallback}
          panelMaxWidth={maxWidth ?? 800}
          panelMinWidth={minWidth}
          panelPreviousWidth={previousWidth ?? minWidth}
          doubleClickCallback={handleDoubleClick}
          isHidden={shouldHideDivider}
        />
      )}
    </>
  );
};
